import React from 'react';
import './PaymentFormModal.css';


const PaymentFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  onBack,
  selectedPlan, 
  formData, 
  handleInputChange,
  formatCardNumber,
  formatExpiry,
  isAnnual = false,
  stepInfo = { current: 3, total: 3, stepName: "Método de Pago" }
}) => {

  if (!isOpen) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    handleInputChange({
      target: { name: 'cardNumber', value: formatted }
    });
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiry(e.target.value);
    handleInputChange({
      target: { name: 'cardExpiry', value: formatted }
    });
  };

  const getCardType = (number) => {
    const num = number.replace(/\s/g, '');
    if (num.startsWith('4')) return 'visa';
    if (num.startsWith('5') || num.startsWith('2')) return 'mastercard';
    if (num.startsWith('3')) return 'amex';
    return 'card';
  };

  // Submit solo llama a onSubmit, el padre controla el flujo y el reseteo de datos
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content payment-form-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="header-title">
              <h2>Completar Pago</h2>
              <div className="step-indicator">
                <span className="step-text">Paso {stepInfo.current} de {stepInfo.total}</span>
                <span className="step-name">{stepInfo.stepName}</span>
              </div>
            </div>
            <button className="modal-close" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="modal-body">
            <div className="payment-summary">
              <h3>Resumen del Pedido</h3>
              <div className="summary-row">
                <span>Plan {selectedPlan?.name}</span>
                <span>
                  {isAnnual 
                    ? `${formatPrice(selectedPlan?.yearlyPrice || 0)}/año`
                    : `${formatPrice(selectedPlan?.monthlyPrice || 0)}/mes`
                  }
                </span>
              </div>
              {isAnnual && (
                <div className="summary-row savings">
                  <span>Ahorras</span>
                  <span className="savings-amount">
                    -{formatPrice((selectedPlan?.monthlyPrice * 12) - (selectedPlan?.yearlyPrice || 0))}
                  </span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total</span>
                <span>
                  {formatPrice(isAnnual ? selectedPlan?.yearlyPrice || 0 : selectedPlan?.monthlyPrice || 0)}
                </span>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="payment-form">
              {/* Método de Pago */}
              <div className="form-section">
                <h4>Método de Pago</h4>
                <div className="payment-methods">
                  <div 
                    className={`payment-method ${formData.paymentMethod === 'card' ? 'active' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'paymentMethod', value: 'card' } })}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span>Tarjeta de Crédito/Débito</span>
                  </div>
                  
                  <div 
                    className={`payment-method ${formData.paymentMethod === 'paypal' ? 'active' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'paymentMethod', value: 'paypal' } })}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M7.076 21c.9 0 1.646-.734 1.72-1.623L9.586 15h2.413c3.902 0 6.633-2.701 6.633-6.633 0-4.127-3.07-7.367-7.367-7.367H6.501C5.12 1 4 2.12 4 3.501v14.998C4 19.881 5.119 21 6.501 21h.575z" fill="#0070ba"/>
                    </svg>
                    <span>PayPal</span>
                  </div>
                  
                  <div 
                    className={`payment-method ${formData.paymentMethod === 'mercadopago' ? 'active' : ''}`}
                    onClick={() => handleInputChange({ target: { name: 'paymentMethod', value: 'mercadopago' } })}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#00b8d4"/>
                      <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z" fill="#00b8d4"/>
                    </svg>
                    <span>MercadoPago</span>
                  </div>
                </div>
              </div>

              {/* Tarjeta de Crédito */}
              {formData.paymentMethod === 'card' && (
                <>
                  <div className="form-section">
                    <h4>Información de la Tarjeta</h4>
                    
                    {/* Tarjeta Visual */}
                    <div className="credit-card-visual">
                      <div className={`credit-card ${getCardType(formData.cardNumber)}`}>
                        <div className="card-background"></div>
                        <div className="card-content">
                          <div className="card-top">
                            <div className="card-chip"></div>
                            <div className="card-type">
                              {getCardType(formData.cardNumber) === 'visa' && (
                                <span className="card-logo visa">VISA</span>
                              )}
                              {getCardType(formData.cardNumber) === 'mastercard' && (
                                <div className="card-logo mastercard">
                                  <div className="mc-circle mc-red"></div>
                                  <div className="mc-circle mc-yellow"></div>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="card-number">
                            {formData.cardNumber || '•••• •••• •••• ••••'}
                          </div>
                          
                          <div className="card-bottom">
                            <div className="card-holder">
                              <div className="card-label">NOMBRE</div>
                              <div className="card-value">
                                {formData.cardName || 'TU NOMBRE'}
                              </div>
                            </div>
                            <div className="card-expiry">
                              <div className="card-label">VÁLIDA</div>
                              <div className="card-value">
                                {formData.cardExpiry || 'MM/AA'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="form-grid">
                      <div className="form-group full-width">
                        <label htmlFor="cardNumber">Número de Tarjeta *</label>
                        <input
                          type="text"
                          id="cardNumber"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleCardNumberChange}
                          maxLength="19"
                          required
                          placeholder="1234 5678 9012 3456"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="cardName">Nombre en la Tarjeta *</label>
                        <input
                          type="text"
                          id="cardName"
                          name="cardName"
                          value={formData.cardName}
                          onChange={handleInputChange}
                          required
                          placeholder="Como aparece en tu tarjeta"
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="cardExpiry">Fecha de Vencimiento *</label>
                        <input
                          type="text"
                          id="cardExpiry"
                          name="cardExpiry"
                          value={formData.cardExpiry}
                          onChange={handleExpiryChange}
                          maxLength="5"
                          required
                          placeholder="MM/AA"
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="cardCVC">CVC *</label>
                        <input
                          type="text"
                          id="cardCVC"
                          name="cardCVC"
                          value={formData.cardCVC}
                          onChange={handleInputChange}
                          maxLength="4"
                          required
                          placeholder="123"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* PayPal */}
              {formData.paymentMethod === 'paypal' && (
                <div className="form-section">
                  <div className="payment-info">
                    <div className="paypal-info">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M7.076 21c.9 0 1.646-.734 1.72-1.623L9.586 15h2.413c3.902 0 6.633-2.701 6.633-6.633 0-4.127-3.07-7.367-7.367-7.367H6.501C5.12 1 4 2.12 4 3.501v14.998C4 19.881 5.119 21 6.501 21h.575z" fill="#0070ba"/>
                      </svg>
                      <h3>PayPal</h3>
                      <p>Serás redirigido a PayPal para completar tu pago de forma segura.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* MercadoPago */}
              {formData.paymentMethod === 'mercadopago' && (
                <div className="form-section">
                  <div className="payment-info">
                    <div className="mercadopago-info">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#00b8d4"/>
                        <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6z" fill="#00b8d4"/>
                      </svg>
                      <h3>MercadoPago</h3>
                      <p>Serás redirigido a MercadoPago para completar tu pago con múltiples opciones disponibles.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="glass-button secondary" onClick={onBack}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Atrás
                </button>
                <button type="submit" className="glass-button primary">
                  {formData.paymentMethod === 'card' ? '💳 Pagar Ahora' : 
                  formData.paymentMethod === 'paypal' ? '🅿️ Continuar con PayPal' :
                  '💰 Continuar con MercadoPago'}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13 7H3m0 0l4-4m-4 4l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
  {/* El modal de confirmación global lo controla el padre (LicenseSection.js) */}
    </>
  );
};

export default PaymentFormModal;